import ExcelJS from "exceljs";
import dayjs from "dayjs";

const saveExcelFile = (buffer, fileName) => {
	const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
	const downloadLink = document.createElement("a");
	downloadLink.href = window.URL.createObjectURL(blob);
	downloadLink.download = fileName;
	downloadLink.click();
};

const toNumber = (value) => {
	const parsed = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
	return Number.isFinite(parsed) ? parsed : 0;
};

const border = {
	top: { style: "thin" },
	left: { style: "thin" },
	bottom: { style: "thin" },
	right: { style: "thin" },
};

const styleCell = (cell, options = {}) => {
	cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
	cell.border = options.border || border;
	cell.font = { bold: Boolean(options.bold), size: options.size || 8 };
	if (options.fill) {
		cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: options.fill } };
	}
};

const formatRawHeader = (field) =>
	String(field || "")
		.replace(/_/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.trim()
		.toUpperCase();

const formatRawValue = (value) => {
	if (value === null || value === undefined) return "";
	if (typeof value === "object") return JSON.stringify(value);
	return value;
};

const formatDownloadMonthRange = (fromDate, toDate) => {
	const fromLabel = dayjs(fromDate).isValid() ? dayjs(fromDate).format("MMM-YYYY") : "unknown-month";
	const toLabel = dayjs(toDate).isValid() ? dayjs(toDate).format("MMM-YYYY") : "unknown-month";
	return dayjs(fromDate).isValid() && dayjs(toDate).isValid() && dayjs(fromDate).isSame(dayjs(toDate), "month")
		? fromLabel
		: `${fromLabel}_to_${toLabel}`;
};

const normalizeExportKey = (value = "") => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const normalizedRowKeyMapCache = new WeakMap();
const monthSortValueCache = new Map();
const parsedMonthLabelCache = new Map();

const getNormalizedRowKeyMap = (row = {}) => {
	if (!row || typeof row !== "object") return {};
	const cachedValue = normalizedRowKeyMapCache.get(row);
	if (cachedValue) return cachedValue;

	const normalizedKeyMap = Object.keys(row).reduce((accumulator, key) => {
		accumulator[normalizeExportKey(key)] = key;
		return accumulator;
	}, {});

	normalizedRowKeyMapCache.set(row, normalizedKeyMap);
	return normalizedKeyMap;
};

const getCandidateValue = (row = {}, candidates = [], fallback = "") => {
	const normalizedKeyMap = getNormalizedRowKeyMap(row);

	for (const candidate of candidates) {
		const matchedKey = normalizedKeyMap[normalizeExportKey(candidate)];
		if (!matchedKey) continue;

		const value = row?.[matchedKey];
		if (value === 0 || value === "0") return value;
		if (value !== undefined && value !== null && String(value).trim() !== "") return value;
	}

	return fallback;
};

const parseExportMonth = (value, fallbackDate) => {
	const cacheKey = `${String(value ?? "")}__${String(fallbackDate ?? "")}`;
	if (parsedMonthLabelCache.has(cacheKey)) return parsedMonthLabelCache.get(cacheKey);

	if (value === null || value === undefined || value === "") {
		const fallbackLabel = dayjs(fallbackDate).isValid() ? dayjs(fallbackDate).format("MMM-YY") : "";
		parsedMonthLabelCache.set(cacheKey, fallbackLabel);
		return fallbackLabel;
	}

	const parsed = dayjs(String(value), ["MMM-YY", "MMM YY", "MMM-YYYY", "MMM YYYY", "MMMM-YYYY", "YYYY-MM", "YYYY-MM-DD"], true);
	if (parsed.isValid()) {
		const formattedValue = parsed.format("MMM-YY");
		parsedMonthLabelCache.set(cacheKey, formattedValue);
		return formattedValue;
	}

	const looseParsed = dayjs(value);
	const formattedValue = looseParsed.isValid() ? looseParsed.format("MMM-YY") : String(value);
	parsedMonthLabelCache.set(cacheKey, formattedValue);
	return formattedValue;
};

const getMonthSortValue = (value, fallbackDate) => {
	const cacheKey = `${String(value ?? "")}__${String(fallbackDate ?? "")}`;
	if (monthSortValueCache.has(cacheKey)) return monthSortValueCache.get(cacheKey);

	const parsed = dayjs(String(value || ""), ["MMM-YY", "MMM YY", "MMM-YYYY", "MMM YYYY", "MMMM-YYYY", "YYYY-MM", "YYYY-MM-DD"], true);
	if (parsed.isValid()) {
		const parsedValue = parsed.valueOf();
		monthSortValueCache.set(cacheKey, parsedValue);
		return parsedValue;
	}

	const looseParsed = dayjs(value || fallbackDate);
	const parsedValue = looseParsed.isValid() ? looseParsed.valueOf() : Number.MAX_SAFE_INTEGER;
	monthSortValueCache.set(cacheKey, parsedValue);
	return parsedValue;
};

const toPercentLabel = (value) => {
	if (value === null || value === undefined || value === "") return "";
	const parsed = toNumber(value);
	if (!parsed) return "0.00%";
	return `${parsed.toFixed(2)}%`;
};

const safeSheetName = (value, fallback = "Sheet") => {
	const sanitized = String(value || fallback)
		.replace(/[\\/*?:[\]]/g, " ")
		.trim();
	return (sanitized || fallback).slice(0, 31);
};

const cofoMainColumns = [
	{ label: "Month", key: "month", width: 11 },
	{ label: "Store Name", key: "storeName", width: 24 },
	{ label: "Store Code", key: "storeCode", width: 14 },
	{ label: "Qty.", key: "qty", width: 8, type: "number" },
	{ label: "MRP Sales", key: "mrpSales", width: 13, type: "number" },
	{ label: "Discount", key: "discount", width: 12, type: "number" },
	{ label: "Discount %", key: "discountPercent", width: 11, type: "percent" },
	{ label: "Realised Sales", key: "realisedSales", width: 13, type: "number" },
	{ label: "Commission on Realised", key: "commissionOnRealised", width: 14, type: "percent" },
	{ label: "Gross Commission", key: "grossCommission", width: 13, type: "number" },
	{ label: "Petty Cash", key: "pettyCash", width: 11, type: "number" },
	{ label: "Salary", key: "salary", width: 11, type: "number" },
	{ label: "Net Commission", key: "netCommission", width: 13, type: "number" },
	{ label: "GST", key: "gst", width: 10, type: "number" },
	{ label: "Total Value", key: "totalValue", width: 12, type: "number" },
];

const cofoSalaryColumns = [
	{ label: "Month", key: "month", width: 11 },
	{ label: "Store Name", key: "storeName", width: 24 },
	{ label: "Store Code", key: "storeCode", width: 14 },
	{ label: "Earned Salary", key: "earnedSalary", width: 13, type: "number" },
	{ label: "Incentive", key: "incentive", width: 11, type: "number" },
	{ label: "Employer ESI", key: "employerEsi", width: 11, type: "number" },
	{ label: "Employer PF", key: "employerPf", width: 11, type: "number" },
	{ label: "Total Liability", key: "totalLiability", width: 13, type: "number" },
];

const cofoPettyCashColumns = [
	{ label: "Month", key: "month", width: 11 },
	{ label: "Store Name", key: "storeName", width: 24 },
	{ label: "Amount", key: "amount", width: 12, type: "number" },
];

const cofoStoreLevelColumns = [
	{ label: "FRANCHISE", key: "franchise", width: 28, type: "text" },
	{ label: "STORE NAME", key: "storeName", width: 24, type: "text" },
	{ label: "STORE CODE", key: "storeCode", width: 14, type: "text" },
	{ label: "QTY", key: "qty", width: 10, type: "number" },
	{ label: "MRP VALUE", key: "mrpValue", width: 14, type: "number" },
	{ label: "DISCOUNT", key: "discount", width: 14, type: "number" },
	{ label: "REALISED SALE", key: "realisedSale", width: 16, type: "number" },
	{ label: "COMMISSION RATE", key: "commissionRate", width: 16, type: "percent" },
	{ label: "COMMISSION VALUE", key: "commissionValue", width: 17, type: "number" },
	{ label: "SALARY", key: "salary", width: 12, type: "number" },
	{ label: "PETTY CASH", key: "pettyCash", width: 13, type: "number" },
	{ label: "NET COMMISSION", key: "netCommission", width: 17, type: "number" },
	{ label: "GST", key: "gst", width: 12, type: "number" },
	{ label: "INVOICE", key: "invoice", width: 14, type: "number" },
];

const cofoMonthLevelStoreColumns = [
	{ label: "MONTH", key: "month", width: 12, type: "text" },
	...cofoStoreLevelColumns,
];

const getCofoFranchiseName = (row = {}) =>
	getCandidateValue(row, ["franchise_name", "franchise", "name", "franchiseName", "label"], "").trim();

const getCofoStoreName = (row = {}) =>
	getCandidateValue(
		row,
		["store_name", "storeName", "site_name", "siteName", "store", "store_display_name", "STORE_NAME", "short_code", "name"],
		""
	).trim();

const getCofoStoreCode = (row = {}) =>
	String(getCandidateValue(row, ["store_code", "storeCode", "sitecode", "site_code", "short_code", "code"], "") || "").trim();

const getCofoMonth = (row = {}, fallbackDate) =>
	parseExportMonth(
		getCandidateValue(
			row,
			[
				"month",
				"month_year",
				"monthYear",
				"month_name",
				"posting_month",
				"date",
				"bill_date",
				"billDate",
				"invoice_date",
				"invoiceDate",
				"sale_date",
				"saleDate",
				"transaction_date",
				"transactionDate",
				"business_date",
				"businessDate",
				"posting_date",
				"postingDate",
				"created_date",
				"createdDate",
			],
			""
		),
		fallbackDate
	);

const buildCofoGroupKey = ({ franchise, month, storeName, storeCode }) =>
	`${normalizeExportKey(franchise)}__${normalizeExportKey(month)}__${normalizeExportKey(storeCode || storeName)}__${normalizeExportKey(storeName)}`;

const groupCofoSalaryRows = (rows = [], fallbackDate) => {
	const grouped = new Map();

	rows.forEach((row, index) => {
		const franchise = getCofoFranchiseName(row);
		const storeName = getCofoStoreName(row) || `Store ${index + 1}`;
		const storeCode = getCofoStoreCode(row);
		const month = getCofoMonth(row, fallbackDate);
		const key = buildCofoGroupKey({ franchise, month, storeName, storeCode });
		const current = grouped.get(key) || {
			franchise,
			month,
			storeName,
			storeCode,
			earnedSalary: 0,
			incentive: 0,
			employerEsi: 0,
			employerPf: 0,
			totalLiability: 0,
		};

		current.storeCode = current.storeCode || storeCode;
		current.earnedSalary += toNumber(getCandidateValue(row, ["earned_salary", "gross_salary", "earnedSalary"]));
		current.incentive += toNumber(getCandidateValue(row, ["incentive"]));
		current.employerEsi += toNumber(getCandidateValue(row, ["employer_esi", "employerESI", "esi"]));
		current.employerPf += toNumber(getCandidateValue(row, ["employer_pf", "employerPF", "pf"]));
		current.totalLiability += toNumber(getCandidateValue(row, ["total_liability", "totalLiability"]));

		grouped.set(key, current);
	});

	return Array.from(grouped.values()).sort((first, second) => {
		const monthDifference = getMonthSortValue(first.month, fallbackDate) - getMonthSortValue(second.month, fallbackDate);
		if (monthDifference !== 0) return monthDifference;
		return String(first.storeName).localeCompare(String(second.storeName));
	});
};

const groupCofoPettyCashRows = (rows = [], fallbackDate) => {
	const grouped = new Map();

	rows.forEach((row, index) => {
		const franchise = getCofoFranchiseName(row);
		const storeName = getCofoStoreName(row) || `Store ${index + 1}`;
		const storeCode = getCofoStoreCode(row);
		const month = getCofoMonth(row, fallbackDate);
		const key = buildCofoGroupKey({ franchise, month, storeName, storeCode });
		const current = grouped.get(key) || {
			franchise,
			month,
			storeName,
			storeCode,
			amount: 0,
		};

		current.storeCode = current.storeCode || storeCode;
		current.amount += toNumber(getCandidateValue(row, ["amount", "petty_cash", "pettyCash"]));
		grouped.set(key, current);
	});

	return Array.from(grouped.values()).sort((first, second) => {
		const monthDifference = getMonthSortValue(first.month, fallbackDate) - getMonthSortValue(second.month, fallbackDate);
		if (monthDifference !== 0) return monthDifference;
		return String(first.storeName).localeCompare(String(second.storeName));
	});
};

const groupCofoReportRows = (rows = [], salaryRows = [], pettyCashRows = [], fallbackDate) => {
	const salaryMap = new Map(
		salaryRows.map((row) => [
			buildCofoGroupKey({ franchise: row.franchise, month: row.month, storeName: row.storeName, storeCode: row.storeCode }),
			row,
		])
	);
	const pettyCashMap = new Map(
		pettyCashRows.map((row) => [
			buildCofoGroupKey({ franchise: row.franchise, month: row.month, storeName: row.storeName, storeCode: row.storeCode }),
			row,
		])
	);
	const grouped = new Map();

	rows.forEach((row, index) => {
		const franchise = getCofoFranchiseName(row);
		const storeName = getCofoStoreName(row) || `Store ${index + 1}`;
		const storeCode = getCofoStoreCode(row);
		const month = getCofoMonth(row, fallbackDate);
		const key = buildCofoGroupKey({ franchise, month, storeName, storeCode });
		const current = grouped.get(key) || {
			franchise,
			month,
			storeName,
			storeCode,
			qty: 0,
			mrpSales: 0,
			discount: 0,
			realisedSales: 0,
			commissionOnRealised: "",
			grossCommission: 0,
		};

		current.storeCode = current.storeCode || storeCode;
		current.qty += toNumber(getCandidateValue(row, ["qty", "quantity", "bill_quantity", "BILL_QUANTITY"]));
		current.mrpSales += toNumber(getCandidateValue(row, ["gross_amount", "mrp_sales", "mrpSales"]));
		current.discount += toNumber(getCandidateValue(row, ["total_discount", "discount"]));
		current.realisedSales += toNumber(getCandidateValue(row, ["realised_value", "realised_sales", "realisedSales"]));
		current.grossCommission += toNumber(getCandidateValue(row, ["payment_margin_amt", "gross_commission", "grossCommission", "commission_amount"]));
		current.commissionOnRealised =
			current.commissionOnRealised ||
			getCandidateValue(row, ["payment_margin_percent", "commission_percent", "commissionOnRealised"], "");

		grouped.set(key, current);
	});

	return Array.from(grouped.values())
		.map((row) => {
			const recordKey = buildCofoGroupKey({
				franchise: row.franchise,
				month: row.month,
				storeName: row.storeName,
				storeCode: row.storeCode,
			});
			const salary = salaryMap.get(recordKey);
			const pettyCash = pettyCashMap.get(recordKey);
			const salaryValue = toNumber(salary?.totalLiability);
			const pettyCashValue = toNumber(pettyCash?.amount);
			const grossCommission = toNumber(row.grossCommission);
			const netCommission = grossCommission - salaryValue - pettyCashValue;
			const gst = netCommission * 0.18;

			return {
				...row,
				discountPercent: row.mrpSales ? (row.discount / row.mrpSales) * 100 : 0,
				pettyCash: pettyCashValue,
				salary: salaryValue,
				netCommission,
				gst,
				totalValue: netCommission + gst,
			};
		})
		.sort((first, second) => {
			const monthDifference = getMonthSortValue(first.month, fallbackDate) - getMonthSortValue(second.month, fallbackDate);
			if (monthDifference !== 0) return monthDifference;
			return String(first.storeName).localeCompare(String(second.storeName));
		});
};

const setCofoCellValue = (cell, column, value) => {
	if (column.type === "percent") {
		cell.value = toPercentLabel(value);
		return;
	}

	if (column.type === "number") {
		cell.value = toNumber(value);
		cell.numFmt = "#,##0";
		return;
	}

	cell.value = value ?? "";
};

const getStoreLevelRows = (rows = []) => {
	const grouped = new Map();

	rows.forEach((row) => {
		const key = `${normalizeExportKey(row.franchise)}__${normalizeExportKey(row.storeCode || row.storeName)}__${normalizeExportKey(row.storeName)}`;
		const current = grouped.get(key) || {
			franchise: row.franchise || "",
			storeName: row.storeName || "",
			storeCode: row.storeCode || "",
			qty: 0,
			mrpValue: 0,
			discount: 0,
			realisedSale: 0,
			commissionValue: 0,
			salary: 0,
			pettyCash: 0,
			netCommission: 0,
			gst: 0,
			invoice: 0,
		};

		current.qty += toNumber(row.qty);
		current.mrpValue += toNumber(row.mrpSales);
		current.discount += toNumber(row.discount);
		current.realisedSale += toNumber(row.realisedSales);
		current.commissionValue += toNumber(row.grossCommission);
		current.salary += toNumber(row.salary);
		current.pettyCash += toNumber(row.pettyCash);
		current.netCommission += toNumber(row.netCommission);
		current.gst += toNumber(row.gst);
		current.invoice += toNumber(row.totalValue);

		grouped.set(key, current);
	});

	return Array.from(grouped.values())
		.map((row) => ({
			...row,
			commissionRate: row.realisedSale ? (row.commissionValue / row.realisedSale) * 100 : 0,
		}))
		.sort((first, second) => {
			const franchiseDifference = String(first.franchise).localeCompare(String(second.franchise));
			if (franchiseDifference !== 0) return franchiseDifference;
			return String(first.storeName).localeCompare(String(second.storeName));
		});
};

const buildStoreLevelTotalRow = (rows = []) => {
	const totals = rows.reduce(
		(accumulator, row) => ({
			qty: accumulator.qty + toNumber(row.qty),
			mrpValue: accumulator.mrpValue + toNumber(row.mrpValue),
			discount: accumulator.discount + toNumber(row.discount),
			realisedSale: accumulator.realisedSale + toNumber(row.realisedSale),
			commissionValue: accumulator.commissionValue + toNumber(row.commissionValue),
			salary: accumulator.salary + toNumber(row.salary),
			pettyCash: accumulator.pettyCash + toNumber(row.pettyCash),
			netCommission: accumulator.netCommission + toNumber(row.netCommission),
			gst: accumulator.gst + toNumber(row.gst),
			invoice: accumulator.invoice + toNumber(row.invoice),
		}),
		{
			qty: 0,
			mrpValue: 0,
			discount: 0,
			realisedSale: 0,
			commissionValue: 0,
			salary: 0,
			pettyCash: 0,
			netCommission: 0,
			gst: 0,
			invoice: 0,
		}
	);

	return {
		franchise: "",
		storeName: "TOTAL",
		storeCode: "",
		qty: totals.qty,
		mrpValue: totals.mrpValue,
		discount: totals.discount,
		realisedSale: totals.realisedSale,
		commissionRate: totals.realisedSale ? (totals.commissionValue / totals.realisedSale) * 100 : 0,
		commissionValue: totals.commissionValue,
		salary: totals.salary,
		pettyCash: totals.pettyCash,
		netCommission: totals.netCommission,
		gst: totals.gst,
		invoice: totals.invoice,
	};
};

const getMonthLevelStoreRows = (rows = [], fallbackDate) => {
	const grouped = new Map();

	rows.forEach((row) => {
		const key = `${normalizeExportKey(row.month)}__${normalizeExportKey(row.franchise)}__${normalizeExportKey(row.storeCode || row.storeName)}__${normalizeExportKey(row.storeName)}`;
		const current = grouped.get(key) || {
			month: row.month || "",
			franchise: row.franchise || "",
			storeName: row.storeName || "",
			storeCode: row.storeCode || "",
			qty: 0,
			mrpValue: 0,
			discount: 0,
			realisedSale: 0,
			commissionValue: 0,
			salary: 0,
			pettyCash: 0,
			netCommission: 0,
			gst: 0,
			invoice: 0,
		};

		current.qty += toNumber(row.qty);
		current.mrpValue += toNumber(row.mrpSales);
		current.discount += toNumber(row.discount);
		current.realisedSale += toNumber(row.realisedSales);
		current.commissionValue += toNumber(row.grossCommission);
		current.salary += toNumber(row.salary);
		current.pettyCash += toNumber(row.pettyCash);
		current.netCommission += toNumber(row.netCommission);
		current.gst += toNumber(row.gst);
		current.invoice += toNumber(row.totalValue);

		grouped.set(key, current);
	});

	return Array.from(grouped.values())
		.map((row) => ({
			...row,
			commissionRate: row.realisedSale ? (row.commissionValue / row.realisedSale) * 100 : 0,
		}))
		.sort((first, second) => {
			const monthDifference = getMonthSortValue(first.month, fallbackDate) - getMonthSortValue(second.month, fallbackDate);
			if (monthDifference !== 0) return monthDifference;

			const franchiseDifference = String(first.franchise).localeCompare(String(second.franchise));
			if (franchiseDifference !== 0) return franchiseDifference;

			return String(first.storeName).localeCompare(String(second.storeName));
		});
};

const buildMonthLevelStoreTotalRow = (rows = []) => {
	const storeLevelTotals = buildStoreLevelTotalRow(rows);
	return {
		month: "TOTAL",
		...storeLevelTotals,
	};
};

const writeStoreLevelSheet = (workbook, rows = [], fromDate, toDate) => {
	if (!rows.length) return;

	const sheet = workbook.addWorksheet(safeSheetName("Store Level Summary", "Store Level"));
	const startCol = 2;
	const endCol = startCol + cofoStoreLevelColumns.length - 1;
	const title = "COFO - Store Level Summary";
	const monthTitle = dayjs(fromDate).isSame(dayjs(toDate), "month")
		? `Month ${dayjs(fromDate).format("MMMM-YYYY")}`
		: `Month ${dayjs(fromDate).format("MMMM-YYYY")} to ${dayjs(toDate).format("MMMM-YYYY")}`;
	const storeLevelRows = getStoreLevelRows(rows);

	sheet.mergeCells(1, startCol, 1, endCol);
	sheet.mergeCells(2, startCol, 2, endCol);
	sheet.getCell(1, startCol).value = title;
	sheet.getCell(2, startCol).value = monthTitle;

	for (let row = 1; row <= 2; row += 1) {
		for (let col = startCol; col <= endCol; col += 1) {
			styleCell(sheet.getCell(row, col), {
				bold: true,
				fill: row === 1 ? "9E480E" : undefined,
				size: row === 1 ? 10 : 9,
			});
			sheet.getCell(row, col).font = {
				...sheet.getCell(row, col).font,
				color: row === 1 ? { argb: "FFFFFFFF" } : undefined,
			};
		}
	}

	cofoStoreLevelColumns.forEach((column, index) => {
		const cell = sheet.getCell(4, startCol + index);
		cell.value = column.label;
		styleCell(cell, { bold: true, fill: "9E480E", size: 8 });
		cell.font = { ...cell.font, color: { argb: "FFFFFFFF" } };
	});

	storeLevelRows.forEach((row, rowIndex) => {
		cofoStoreLevelColumns.forEach((column, columnIndex) => {
			const cell = sheet.getCell(5 + rowIndex, startCol + columnIndex);
			setCofoCellValue(cell, column, row?.[column.key]);
			styleCell(cell, { size: 8 });
		});
	});

	const totalRow = buildStoreLevelTotalRow(storeLevelRows);
	const totalRowNumber = 5 + storeLevelRows.length;
	cofoStoreLevelColumns.forEach((column, index) => {
		const cell = sheet.getCell(totalRowNumber, startCol + index);
		setCofoCellValue(cell, column, totalRow?.[column.key]);
		styleCell(cell, { bold: true, fill: "DAEEF3", size: 8 });
	});

	cofoStoreLevelColumns.forEach((column, index) => {
		sheet.getColumn(startCol + index).width = column.width;
	});

	applyCofoSheetFrame(sheet, startCol, endCol, totalRowNumber);
};

const writeMonthLevelStoreSheet = (workbook, rows = [], fromDate, toDate) => {
	if (!rows.length) return;

	const sheet = workbook.addWorksheet(safeSheetName("Month Level Store Summary", "Month Level"));
	const startCol = 2;
	const endCol = startCol + cofoMonthLevelStoreColumns.length - 1;
	const title = "COFO - Month Level Store Summary";
	const monthTitle = dayjs(fromDate).isSame(dayjs(toDate), "month")
		? `Month ${dayjs(fromDate).format("MMMM-YYYY")}`
		: `Month ${dayjs(fromDate).format("MMMM-YYYY")} to ${dayjs(toDate).format("MMMM-YYYY")}`;
	const monthLevelRows = getMonthLevelStoreRows(rows, fromDate);

	sheet.mergeCells(1, startCol, 1, endCol);
	sheet.mergeCells(2, startCol, 2, endCol);
	sheet.getCell(1, startCol).value = title;
	sheet.getCell(2, startCol).value = monthTitle;

	for (let row = 1; row <= 2; row += 1) {
		for (let col = startCol; col <= endCol; col += 1) {
			styleCell(sheet.getCell(row, col), {
				bold: true,
				fill: row === 1 ? "9E480E" : undefined,
				size: row === 1 ? 10 : 9,
			});
			sheet.getCell(row, col).font = {
				...sheet.getCell(row, col).font,
				color: row === 1 ? { argb: "FFFFFFFF" } : undefined,
			};
		}
	}

	cofoMonthLevelStoreColumns.forEach((column, index) => {
		const cell = sheet.getCell(4, startCol + index);
		cell.value = column.label;
		styleCell(cell, { bold: true, fill: "9E480E", size: 8 });
		cell.font = { ...cell.font, color: { argb: "FFFFFFFF" } };
	});

	monthLevelRows.forEach((row, rowIndex) => {
		cofoMonthLevelStoreColumns.forEach((column, columnIndex) => {
			const cell = sheet.getCell(5 + rowIndex, startCol + columnIndex);
			setCofoCellValue(cell, column, row?.[column.key]);
			styleCell(cell, { size: 8 });
		});
	});

	const totalRow = buildMonthLevelStoreTotalRow(monthLevelRows);
	const totalRowNumber = 5 + monthLevelRows.length;
	cofoMonthLevelStoreColumns.forEach((column, index) => {
		const cell = sheet.getCell(totalRowNumber, startCol + index);
		setCofoCellValue(cell, column, totalRow?.[column.key]);
		styleCell(cell, { bold: true, fill: "DAEEF3", size: 8 });
	});

	cofoMonthLevelStoreColumns.forEach((column, index) => {
		sheet.getColumn(startCol + index).width = column.width;
	});

	applyCofoSheetFrame(sheet, startCol, endCol, totalRowNumber);
};

const writeCofoTable = (sheet, startRow, startCol, columns, rows, totalRowBuilder) => {
	columns.forEach((column, index) => {
		const cell = sheet.getCell(startRow, startCol + index);
		cell.value = column.label;
		styleCell(cell, { bold: true, fill: "EBF1DE", size: 8 });
	});

	rows.forEach((row, rowIndex) => {
		columns.forEach((column, columnIndex) => {
			const cell = sheet.getCell(startRow + rowIndex + 1, startCol + columnIndex);
			setCofoCellValue(cell, column, row?.[column.key]);
			styleCell(cell, { size: 8 });
		});
	});

	const totalRowIndex = startRow + rows.length + 1;
	const totalRow = typeof totalRowBuilder === "function" ? totalRowBuilder(rows) : null;

	if (totalRow) {
		columns.forEach((column, index) => {
			const cell = sheet.getCell(totalRowIndex, startCol + index);
			setCofoCellValue(cell, column, totalRow?.[column.key]);
			styleCell(cell, { bold: true, fill: "DAEEF3", size: 8 });
		});
	}

	return totalRow ? totalRowIndex + 1 : totalRowIndex;
};

const buildCofoMainTotalRow = (rows = []) => {
	const totals = rows.reduce(
		(accumulator, row) => ({
			qty: accumulator.qty + toNumber(row.qty),
			mrpSales: accumulator.mrpSales + toNumber(row.mrpSales),
			discount: accumulator.discount + toNumber(row.discount),
			realisedSales: accumulator.realisedSales + toNumber(row.realisedSales),
			grossCommission: accumulator.grossCommission + toNumber(row.grossCommission),
			pettyCash: accumulator.pettyCash + toNumber(row.pettyCash),
			salary: accumulator.salary + toNumber(row.salary),
			netCommission: accumulator.netCommission + toNumber(row.netCommission),
			gst: accumulator.gst + toNumber(row.gst),
			totalValue: accumulator.totalValue + toNumber(row.totalValue),
		}),
		{
			qty: 0,
			mrpSales: 0,
			discount: 0,
			realisedSales: 0,
			grossCommission: 0,
			pettyCash: 0,
			salary: 0,
			netCommission: 0,
			gst: 0,
			totalValue: 0,
		}
	);

	return {
		month: "",
		storeName: "Total",
		storeCode: "",
		qty: totals.qty,
		mrpSales: totals.mrpSales,
		discount: totals.discount,
		discountPercent: totals.mrpSales ? (totals.discount / totals.mrpSales) * 100 : 0,
		realisedSales: totals.realisedSales,
		commissionOnRealised: "",
		grossCommission: totals.grossCommission,
		pettyCash: totals.pettyCash,
		salary: totals.salary,
		netCommission: totals.netCommission,
		gst: totals.gst,
		totalValue: totals.totalValue,
	};
};

const buildCofoSalaryTotalRow = (rows = []) => ({
	month: "",
	storeName: "Total",
	storeCode: "",
	earnedSalary: rows.reduce((sum, row) => sum + toNumber(row.earnedSalary), 0),
	incentive: rows.reduce((sum, row) => sum + toNumber(row.incentive), 0),
	employerEsi: rows.reduce((sum, row) => sum + toNumber(row.employerEsi), 0),
	employerPf: rows.reduce((sum, row) => sum + toNumber(row.employerPf), 0),
	totalLiability: rows.reduce((sum, row) => sum + toNumber(row.totalLiability), 0),
});

const buildCofoPettyCashTotalRow = (rows = []) => ({
	month: "",
	storeName: "Total",
	storeCode: "",
	amount: rows.reduce((sum, row) => sum + toNumber(row.amount), 0),
});

const applyCofoSheetFrame = (sheet, startCol, endCol, endRow) => {
	for (let row = 1; row <= endRow; row += 1) {
		for (let col = startCol; col <= endCol; col += 1) {
			const cell = sheet.getCell(row, col);
			const existing = cell.border || {};
			cell.border = {
				...existing,
				top: row === 1 ? { style: "medium" } : existing.top,
				bottom: row === endRow ? { style: "medium" } : existing.bottom,
				left: col === startCol ? { style: "medium" } : existing.left,
				right: col === endCol ? { style: "medium" } : existing.right,
			};
		}
	}
};

const writeCofoRawSheet = (workbook, sheetName, rows = [], excludedFields = [], fallbackDate) => {
	if (!rows.length) return;

	const sheet = workbook.addWorksheet(safeSheetName(sheetName, "Raw Data"));
	const excludedKeySet = new Set(excludedFields.map((field) => normalizeExportKey(field)));
	const orderedRows = [...rows].sort((first, second) => {
		const firstMonthValue = getCandidateValue(first, ["month", "month_year", "monthYear", "date", "month_name", "posting_month"], "");
		const secondMonthValue = getCandidateValue(second, ["month", "month_year", "monthYear", "date", "month_name", "posting_month"], "");
		const monthDifference = getMonthSortValue(firstMonthValue, fallbackDate) - getMonthSortValue(secondMonthValue, fallbackDate);
		if (monthDifference !== 0) return monthDifference;

		const firstStoreName = getCandidateValue(first, ["store_name", "storeName", "site_name", "siteName", "store", "name"], "");
		const secondStoreName = getCandidateValue(second, ["store_name", "storeName", "site_name", "siteName", "store", "name"], "");
		return String(firstStoreName).localeCompare(String(secondStoreName));
	});
	const columns = Array.from(
		new Set(
			orderedRows.flatMap((row) =>
				Object.keys(row || {}).filter((key) => !excludedKeySet.has(normalizeExportKey(key)))
			)
		)
	);

	const columnWidths = columns.map((column) => formatRawHeader(column).length);
	sheet.columns = columns.map((column, index) => ({
		header: formatRawHeader(column),
		key: column,
		width: 12,
	}));

	const rowValues = orderedRows.map((row) =>
		columns.map((column, columnIndex) => {
			const formattedValue = formatRawValue(row?.[column]);
			columnWidths[columnIndex] = Math.max(columnWidths[columnIndex], String(formattedValue || "").length);
			return formattedValue;
		})
	);

	sheet.addRows(rowValues);

	sheet.getRow(1).eachCell((cell) => {
		styleCell(cell, { bold: true, fill: "EBF1DE", size: 8 });
	});

	for (let rowIndex = 2; rowIndex <= sheet.rowCount; rowIndex += 1) {
		sheet.getRow(rowIndex).eachCell((cell) => {
			styleCell(cell, { size: 8 });
		});
	}

	columns.forEach((column, index) => {
		sheet.getColumn(index + 1).width = Math.min(Math.max(columnWidths[index] + 2, 12), 40);
	});
};

export const downloadCofoFranchiseReportExcel = async ({
	reportRows = [],
	salaryRows = [],
	pettyCashRows = [],
	fromDate,
	toDate,
	selectedFranchises = [],
}) => {
	const workbook = new ExcelJS.Workbook();
	const groupedSalaryRows = groupCofoSalaryRows(salaryRows, fromDate);
	const groupedPettyCashRows = groupCofoPettyCashRows(pettyCashRows, fromDate);
	const groupedReportRows = groupCofoReportRows(reportRows, groupedSalaryRows, groupedPettyCashRows, fromDate);

	const franchiseNames = Array.from(
		new Set(
			[
				...selectedFranchises,
				...groupedReportRows.map((row) => row.franchise),
				...groupedSalaryRows.map((row) => row.franchise),
				...groupedPettyCashRows.map((row) => row.franchise),
			].filter(Boolean)
		)
	);

	if (!franchiseNames.length) {
		const emptySheet = workbook.addWorksheet("COFO Report");
		emptySheet.getCell(1, 1).value = "No data available for the selected filters.";
	}

	franchiseNames.forEach((franchiseName) => {
		const sheet = workbook.addWorksheet(safeSheetName(franchiseName, "COFO"));
		const startCol = 2;
		const mainEndCol = startCol + cofoMainColumns.length - 1;
		const pettyCashStartCol = 11;
		const monthTitle = dayjs(fromDate).isSame(dayjs(toDate), "month")
			? `Month ${dayjs(fromDate).format("MMMM-YYYY")}`
			: `Month ${dayjs(fromDate).format("MMMM-YYYY")} to ${dayjs(toDate).format("MMMM-YYYY")}`;
		const franchiseReportRows = groupedReportRows.filter((row) => normalizeExportKey(row.franchise) === normalizeExportKey(franchiseName));
		const franchiseSalaryRows = groupedSalaryRows.filter((row) => normalizeExportKey(row.franchise) === normalizeExportKey(franchiseName));
		const franchisePettyCashRows = groupedPettyCashRows.filter((row) => normalizeExportKey(row.franchise) === normalizeExportKey(franchiseName));

		sheet.mergeCells(1, startCol, 1, mainEndCol);
		sheet.mergeCells(2, startCol, 2, mainEndCol);
		sheet.getCell(1, startCol).value = `${franchiseName} - Commission Working`;
		sheet.getCell(2, startCol).value = monthTitle;

		for (let row = 1; row <= 2; row += 1) {
			for (let col = startCol; col <= mainEndCol; col += 1) {
				styleCell(sheet.getCell(row, col), {
					bold: true,
					fill: row === 1 ? "B8CCE4" : undefined,
					size: row === 1 ? 10 : 9,
				});
			}
		}

		let contentEndRow = writeCofoTable(sheet, 4, startCol, cofoMainColumns, franchiseReportRows, buildCofoMainTotalRow);
		let nextRow = contentEndRow + 1;
		let salaryTableStartRow = nextRow + 1;

		if (franchiseSalaryRows.length) {
			contentEndRow = writeCofoTable(sheet, salaryTableStartRow, startCol, cofoSalaryColumns, franchiseSalaryRows, buildCofoSalaryTotalRow);
			nextRow = contentEndRow + 1;
		}

		if (franchisePettyCashRows.length) {
			const pettyCashStartRow = franchiseSalaryRows.length ? salaryTableStartRow : nextRow + 1;
			contentEndRow = Math.max(
				contentEndRow,
				writeCofoTable(sheet, pettyCashStartRow, pettyCashStartCol, cofoPettyCashColumns, franchisePettyCashRows, buildCofoPettyCashTotalRow)
			);
		}

		const footerRow = contentEndRow + 2;
		sheet.mergeCells(footerRow, startCol, footerRow, mainEndCol);
		const noteCell = sheet.getCell(footerRow, startCol);
		noteCell.value = "Note*: This is a system-generated report.";
		noteCell.alignment = { vertical: "middle", horizontal: "left" };
		noteCell.font = { size: 8, bold: true };

		cofoMainColumns.forEach((column, index) => {
			sheet.getColumn(startCol + index).width = column.width;
		});
		cofoSalaryColumns.forEach((column, index) => {
			sheet.getColumn(startCol + index).width = Math.max(sheet.getColumn(startCol + index).width || 0, column.width);
		});
		cofoPettyCashColumns.forEach((column, index) => {
			sheet.getColumn(pettyCashStartCol + index).width = Math.max(sheet.getColumn(pettyCashStartCol + index).width || 0, column.width);
		});

		applyCofoSheetFrame(sheet, startCol, mainEndCol, footerRow);
	});

	writeStoreLevelSheet(workbook, groupedReportRows, fromDate, toDate);

	writeCofoRawSheet(workbook, "Raw Report", reportRows, [], fromDate);
	writeCofoRawSheet(workbook, "Raw Salary", salaryRows, ["updated_by", "created_date"], fromDate);
	writeCofoRawSheet(workbook, "Raw Petty Cash", pettyCashRows, ["updated_by", "created_date"], fromDate);

	const fileName = `cofo_report_${String(formatDownloadMonthRange(fromDate, toDate)).replace(/[^a-z0-9_-]/gi, "_")}.xlsx`;
	const buffer = await workbook.xlsx.writeBuffer();
	saveExcelFile(buffer, fileName.toLowerCase());
};

export const downloadCofoMonthLevelStoreSummaryExcel = async ({
	reportRows = [],
	salaryRows = [],
	pettyCashRows = [],
	fromDate,
	toDate,
}) => {
	const workbook = new ExcelJS.Workbook();
	const groupedSalaryRows = groupCofoSalaryRows(salaryRows, fromDate);
	const groupedPettyCashRows = groupCofoPettyCashRows(pettyCashRows, fromDate);
	const groupedReportRows = groupCofoReportRows(reportRows, groupedSalaryRows, groupedPettyCashRows, fromDate);

	if (!groupedReportRows.length) {
		const emptySheet = workbook.addWorksheet("Month Level Store Summary");
		emptySheet.getCell(1, 1).value = "No data available for the selected filters.";
	} else {
		writeMonthLevelStoreSheet(workbook, groupedReportRows, fromDate, toDate);
	}

	const fileName = `cofo_month_level_store_summary_${String(formatDownloadMonthRange(fromDate, toDate)).replace(/[^a-z0-9_-]/gi, "_")}.xlsx`;
	const buffer = await workbook.xlsx.writeBuffer();
	saveExcelFile(buffer, fileName.toLowerCase());
};
